import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { InvoiceLineEditor } from '@/components/invoices/InvoiceLineEditor';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { NotFound } from '@/routes/NotFound';
import { InvoiceLines } from '@/routes/InvoiceDetail';
import {
  InvoiceNotFoundError,
  InvoiceNotPermittedError,
  useInvoice,
} from '@/routes/invoices/useInvoices';

/**
 * A draft invoice's lines, for the technician who raised it (M2-10; Kyle,
 * 2026-10-02): reached from the job page's "Invoice #N · Draft", and nothing
 * but the lines — no list, no aging, no Mark as sent, no Void, which stay the
 * office's (`/invoices/:id`).
 *
 * The API decides who reaches it: a technician on the job, and only while the
 * invoice is a DRAFT. Once the office sends it, the read is a 403 with the
 * API's own sentence ("…only the office can open it now"), shown here in place
 * of the lines rather than as a generic failure. The lines are edited with the
 * office's editor (`InvoiceLineEditor`): an explicit Save, the invoice's own
 * copies, the job's lines untouched.
 */
export function InvoiceDraftLines() {
  const { t } = useTranslation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const invoice = useInvoice(invoiceId);
  const [editing, setEditing] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  if (invoice.error instanceof InvoiceNotFoundError) {
    return <NotFound />;
  }

  if (invoice.isPending) {
    return <LoadingState label={t('invoices.detail.loading')} />;
  }

  if (invoice.error instanceof InvoiceNotPermittedError) {
    return (
      <EmptyState
        title={t('invoices.draftLines.notPermittedTitle')}
        description={invoice.error.message || t('invoices.draftLines.notPermittedDescription')}
      />
    );
  }

  if (invoice.isError) {
    return (
      <ErrorState
        title={t('invoices.detail.loadErrorTitle')}
        description={t('invoices.detail.loadErrorDescription')}
        onRetry={() => void invoice.refetch()}
      />
    );
  }

  const i = invoice.data;
  const draft = i.status === 'Draft';

  return (
    <section className="space-y-6">
      <div className="space-y-1">
        {i.jobId ? (
          <Link
            to={`/jobs/${i.jobId}`}
            className="text-sm text-[var(--color-muted-foreground)] hover:underline"
          >
            {t('invoices.draftLines.back')}
          </Link>
        ) : null}
        <h1 className="text-xl font-semibold">
          {t('invoices.numbered', { number: Number(i.number) })}
        </h1>
        <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="draft-lines-help">
          {draft ? t('invoices.draftLines.help') : t('invoices.draftLines.notADraft')}
        </p>
      </div>

      {editing && draft ? (
        <InvoiceLineEditor
          invoice={i}
          onDone={(saved) => {
            setEditing(false);
            if (saved) setAnnouncement(t('invoices.edit.saved'));
          }}
        />
      ) : (
        <>
          <InvoiceLines invoice={i} />
          {draft ? (
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setEditing(true)}
            >
              {t('invoices.edit.open')}
            </Button>
          ) : null}
        </>
      )}

      <p role="status" aria-live="polite" className="sr-only" data-testid="announcement">
        {announcement}
      </p>
    </section>
  );
}
